import { useState } from "react";
import { CheckCircle2, Mail, MessageSquareText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Layout } from "@/components/Layout";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { api, formatApiError } from "@/lib/api";

const initial = { name: "", email: "", business: "", message: "" };

export function ContactPage() {
  const [form, setForm] = useState(initial);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const update = (field) => (event) => setForm((value) => ({ ...value, [field]: event.target.value }));
  const submit = async (event) => {
    event.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm(initial);
      toast.success("Message saved");
    } catch (error) {
      toast.error(formatApiError(error, "Your message could not be sent"));
    } finally {
      setSending(false);
    }
  };

  return (
    <Layout>
      <Seo title="Contact" description="Contact Operandi Studio with questions about digital products, orders, delivery, or future tools." />
      <section className="texture-noise px-6 pb-24 pt-36 sm:pt-40">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.8fr_1.2fr]">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.24em] text-amber-700">Contact</p>
            <h1 className="mt-4 font-heading text-4xl font-extrabold tracking-[-0.04em] sm:text-5xl">Ask a practical question.</h1>
            <p className="mt-6 text-lg leading-9 text-[#525866]">Use this form for product questions, delivery issues, refund requests, partnership notes, or ideas for future small-business tools.</p>
            <div className="mt-10 space-y-4">
              <div className="flex items-center gap-4 rounded-3xl border border-[#E6E2DA] bg-white p-5"><Mail className="h-5 w-5 text-amber-700" /><span className="text-sm font-semibold">Replies use the email address you provide.</span></div>
              <div className="flex items-center gap-4 rounded-3xl border border-[#E6E2DA] bg-white p-5"><MessageSquareText className="h-5 w-5 text-amber-700" /><span className="text-sm font-semibold">Messages are stored for the owner dashboard.</span></div>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <form onSubmit={submit} className="rounded-[2.5rem] border border-[#E6E2DA] bg-white p-6 shadow-[0_24px_80px_rgba(17,19,21,0.08)] md:p-10" data-testid="contact-form">
              {sent && (
                <div data-testid="contact-success-message" className="mb-6 flex items-center gap-3 rounded-2xl bg-[#3F5E4D]/10 p-4 text-sm font-semibold text-[#3F5E4D]">
                  <CheckCircle2 className="h-5 w-5" /> Your message has been saved.
                </div>
              )}
              <div className="grid gap-5 sm:grid-cols-2">
                <label className="grid gap-2 text-sm font-semibold">Name
                  <Input required value={form.name} onChange={update("name")} data-testid="contact-name-input" className="h-12 rounded-2xl" />
                </label>
                <label className="grid gap-2 text-sm font-semibold">Email
                  <Input required type="email" value={form.email} onChange={update("email")} data-testid="contact-email-input" className="h-12 rounded-2xl" />
                </label>
              </div>
              <label className="mt-5 grid gap-2 text-sm font-semibold">Business name <span className="font-normal text-[#8C92A0]">(optional)</span>
                <Input value={form.business} onChange={update("business")} data-testid="contact-business-input" className="h-12 rounded-2xl" />
              </label>
              <label className="mt-5 grid gap-2 text-sm font-semibold">Message
                <Textarea required minLength={10} value={form.message} onChange={update("message")} data-testid="contact-message-input" className="min-h-40 rounded-2xl" placeholder="Tell us what you need help with…" />
              </label>
              <Button type="submit" disabled={sending} className="mt-7 h-12 w-full rounded-full bg-[#111315] text-white hover:bg-[#2a2e33]" data-testid="contact-submit-button">
                {sending ? "Sending…" : "Send message"}
              </Button>
            </form>
          </Reveal>
        </div>
      </section>
    </Layout>
  );
}
